import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import luxPlusGlossaryMessages from 'i18n/luxPlusGlossaryMessages'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Pane from 'components/Common/Pane'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import clsx from 'clsx'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import zIndex from 'styles/tools/z-index'
import ModalContext from 'contexts/ModalContext'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LuxPlusLogo from '../Logos/LuxPlusLogo'
import LuxPlusManageAccountModal from '../LuxPlusManageAccount/LuxPlusManageAccountModal'
import useLuxPlusPreferredPaymentCard from 'luxPlus/hooks/useLuxPlusPreferredPaymentMethod'
import LuxPlusLoggedInOffersTabSection from './LuxPlusLoggedInOffersTabSection'
import LuxPlusLoggedInPartnerOffersTabSection from './LuxPlusLoggedInPartnerOffersTabSection'
import LuxPlusLoggedInBenefitsTabSection from './LuxPlusLoggedInBenefitsTabSection'
import { useAppSelector } from 'hooks/reduxHooks'
import { getTimeBasedGreeting } from 'luxPlus/lib/getTimeBasedGreeting'
import getLuxPlusTotalValueSaved from 'luxPlus/selectors/benefits/getLuxPlusTotalValueSaved'
import getLuxPlusRedeemedBenefits from 'luxPlus/selectors/benefits/getLuxPlusRedeemedBenefits'
import usePartnerPromoOffers from 'luxPlus/hooks/usePartnerPromoOffers'
import useLuxPlusLandingTab from 'luxPlus/hooks/useLuxPlusLandingTab'
import getLuxPlusCardExpiresBeforeRenewal from 'luxPlus/lib/getLuxPlusCardExpiresBeforeRenewal'
import { getIsPendingRenewalLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { getLuxPlusSubscriptionRenewalDate } from 'selectors/luxPlusSelectors'
import { useIntlDateFormatter, daysInPast } from 'lib/datetime/dateUtils'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import type { SegmentedOptionsGroupOnValueChangeHandler } from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import variables from 'styles/vendor/bootstrap/variables'
import { scrollToId } from 'lib/window/scrollUtils'
import LuxPlusLandingNavigationProvider from 'luxPlus/contexts/LuxPlusLandingNavigationProvider'
import { trackClientEvent } from 'analytics/analytics'
import { type LuxPlusLandingMemberTab } from 'luxPlus/contexts/LuxPlusLandingNavigationContext'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import LuxPlusMonthTakeoverHomeBanner from 'takeovers/luxPlusMonthTakeover/components/LuxPlusMonthTakeoverHomeBanner'

const HeroSheet = styled(Sheet)`
  background: linear-gradient(90deg, ${props => props.theme.palette.product.luxPlus.background} 0%, #4C82DF 100%);
`

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(32)};
  align-items: center;

  ${mediaQueryUp.tablet} {
    &.has-right-column {
      grid-template-columns: 1fr 1fr;
    }
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    &.has-right-column {
      align-items: flex-start;
      text-align: start;
    }
  }
`

const RightColumn = styled.div`
  display: flex;
  justify-content: center;
  min-width: 0;
`

const NavSection = styled(Pane)`
  position: sticky;
  z-index: ${zIndex.stickyHeaderBanner};
  top: 0;
  padding-block: ${rem(32)} ${rem(20)};
  padding-inline: 0;
`

const HomeBannerSpacing = styled.div`
  margin-block: ${rem(10)};

  ${mediaQueryUp.desktop} {
    margin-block: ${rem(16)};
  }
`

const HomeBannerLayoutContainer = styled(LayoutContainer)`
  padding-inline: 0;

  ${mediaQueryUp.desktop} {
    padding-inline: ${rem(variables.gutterWidth / 2)};
  }
`

const STICKY_NAV_SCROLL_OFFSET = -100
const MEMBER_TABS: ReadonlyArray<LuxPlusLandingMemberTab> = ['best-offers', 'partner-offers', 'all-benefits']

interface Props {
  children?: React.ReactNode
}

function LuxPlusMemberLandingLayout({ children }: Props) {
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const [stickyRef, notSticky] = useInView()
  const { activeTab, navigateToTab } = useLuxPlusLandingTab(MEMBER_TABS, 'best-offers')
  const isMobile = useScreenSizeOnly('mobile')

  const showModal = useContext(ModalContext)
  const currentSiteTakeover = useCurrentSiteTakeover()
  const isLuxPlusMonthTakeover = currentSiteTakeover?.id === LUX_PLUS_MONTH_TAKEOVER_ID

  const firstName = useAppSelector(state => state.auth.account.givenName)
  const subscriptionItem = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item)
  const memberSince = subscriptionItem?.memberSince
  const hasAutoRenewal = subscriptionItem?.renewalStatus === 'ALLOW_RENEWAL'
  const isPendingRenewalLuxPlusMember = useAppSelector(getIsPendingRenewalLuxPlusMember)
  const subscriptionRenewalDate = useAppSelector(getLuxPlusSubscriptionRenewalDate)
  const totalSavings = useAppSelector(getLuxPlusTotalValueSaved)
  const confirmedItems = useAppSelector(getLuxPlusRedeemedBenefits)

  const renewalDate = subscriptionRenewalDate ? new Date(subscriptionRenewalDate) : undefined
  const overDueInDays = renewalDate ? daysInPast(renewalDate) : 0

  const {
    isActiveMemberRenewing,
    isActiveMemberNotRenewing,
    isPendingRenewalAndNotRenewing,
    isPendingRenewalAndIsRenewing,
  } = useMemo(() => ({
    isActiveMemberRenewing: !isPendingRenewalLuxPlusMember && hasAutoRenewal,
    isActiveMemberNotRenewing: !isPendingRenewalLuxPlusMember && !hasAutoRenewal,
    isPendingRenewalAndNotRenewing: isPendingRenewalLuxPlusMember && !hasAutoRenewal,
    isPendingRenewalAndIsRenewing: isPendingRenewalLuxPlusMember && hasAutoRenewal,
  }), [isPendingRenewalLuxPlusMember, hasAutoRenewal])

  const handleViewActivity = useCallback(() => {
    navigateToTab('all-benefits')
    requestAnimationFrame(() => {
      scrollToId('luxplus-your-activity', STICKY_NAV_SCROLL_OFFSET)
    })
  }, [navigateToTab])

  const handleManageMembership = useCallback(() => {
    showModal(<LuxPlusManageAccountModal />)
  }, [showModal])

  const handleUpdatePaymentMethod = useCallback(() => {
    trackClientEvent({
      subject: 'lux_plus_card_expiry_warning',
      action: 'clicked',
      category: LUXURY_PLUS.ANALYTICS_CATEGORY,
      type: 'interaction',
    })
    showModal(<LuxPlusManageAccountModal
      initialMode="payment-method-update"
      onPaymentMethodUpdateSuccess={() => {
        trackClientEvent({
          subject: 'lux_plus_card_expiry_warning',
          action: 'payment_method_updated',
          category: LUXURY_PLUS.ANALYTICS_CATEGORY,
          type: 'interaction',
        })
      }}
    />)
  }, [showModal])

  const preferredPaymentCard = useLuxPlusPreferredPaymentCard()
  const card = preferredPaymentCard?.data?.card
  const isCardExpiringBeforeRenewal = !isPendingRenewalLuxPlusMember && hasAutoRenewal &&
    getLuxPlusCardExpiresBeforeRenewal(card, renewalDate)

  useEffect(() => {
    if (isCardExpiringBeforeRenewal) {
      trackClientEvent({
        subject: 'lux_plus_card_expiry_warning',
        action: 'impressed',
        category: LUXURY_PLUS.ANALYTICS_CATEGORY,
        type: 'nonInteraction',
      })
    }
  }, [isCardExpiringBeforeRenewal])

  const partnerOffers = usePartnerPromoOffers()
  const showPartnerOffersTab = partnerOffers.length >= 3

  const handleTabPartnerOffers = useCallback(() => {
    navigateToTab('partner-offers')
    requestAnimationFrame(() => {
      scrollToId('partner_offers_list', STICKY_NAV_SCROLL_OFFSET)
    })
  }, [navigateToTab])

  const handleTabChange = useCallback<SegmentedOptionsGroupOnValueChangeHandler<LuxPlusLandingMemberTab>>((tab) => {
    if (tab === 'partner-offers') {
      handleTabPartnerOffers()
      return
    }
    navigateToTab(tab)
  }, [handleTabPartnerOffers, navigateToTab])

  const bestOffersLabel = isMobile
    ? intl.formatMessage({
        id: '0W7rqU6hFEy1W4IojqMa',
        defaultMessage: 'Top offers',
        description: 'Mobile tab label for top LuxPlus offers on member landing page',
      })
    : intl.formatMessage({
        id: 'boIot3qKZGZqtOVa1u3r',
        defaultMessage: 'Best {programName} offers',
        description: 'Desktop tab label for best LuxPlus offers on member landing page',
      }, { programName: LUXURY_PLUS.PROGRAM_NAME })

  const greetingWithFirstName = intl.formatMessage({
    id: 'CIqe766T1bi5BRydE3gd',
    defaultMessage: '{greeting}, {firstName}',
    description: 'Personalised time-based greeting with user name on LuxPlus member landing page',
  }, { greeting: getTimeBasedGreeting(intl), firstName })

  const hasRightColumn = !!children

  return (
    <>
      <HeroSheet padding="40 20" paletteType="inverse">
        <LayoutContainer>
          <HeroGrid className={clsx({ 'has-right-column': hasRightColumn })}>
            <LeftColumn className={clsx({ 'has-right-column': hasRightColumn })}>
              <LuxPlusLogo height={20} />
              <HeadingTextBlock variant="heading2">
                {firstName ? greetingWithFirstName : getTimeBasedGreeting(intl)}
              </HeadingTextBlock>
              {totalSavings > 0 && <HeadingTextBlock variant="heading4" colour="highlight-primary">
                <FormattedMessage
                  id="LuBiPBqatV_mWE0dswcs"
                  defaultMessage="You've saved {amount} since joining."
                  description="Shows total savings amount for LuxPlus member on landing page hero"
                  values={{ amount: <FormatCurrency format="roundedDollar" value={totalSavings} /> }}
                />
              </HeadingTextBlock>}
              <Group direction="vertical" gap={4}>
                {memberSince && <BodyText variant="medium" colour="normal-contrast">
                  {intl.formatMessage(
                    {
                      id: 'FaTPeMFJls4qOl2pGdfi',
                      defaultMessage: 'Member since {date}',
                      description: 'Shows the date when the user became a LuxPlus member on account page',
                    },
                    { date: intlDateFormatter(memberSince, INTL_DMY_CASUAL_FORMAT) },
                  )}
                </BodyText>}
                {!!subscriptionItem && <Group direction="horizontal" gap={8} verticalAlign="center">
                  <BodyText variant="medium" colour="normal-contrast" as="span">
                    {(isActiveMemberRenewing && subscriptionItem.nextChargeAttemptDate !== undefined) && <FormattedMessage
                      id="80ox8ABrnAp9j3gAVx6l"
                      defaultMessage="Membership renews on {date}"
                      description="Shows the membership renewal date on LuxPlus account page"
                      values={{ date: <FormatDateTime value={subscriptionItem.nextChargeAttemptDate} format="dmy-casual" /> }}
                    />}
                    {isActiveMemberNotRenewing && <FormattedMessage
                      id="pXro8ucZyagEVM27RtWh"
                      defaultMessage="Membership ends on {date}"
                      description="Shows the membership expiry date when auto-renewal is off on LuxPlus account page"
                      values={{ date: <FormatDateTime value={subscriptionItem.expiryDate} format="dmy-casual" /> }}
                    />}
                    {(isPendingRenewalAndIsRenewing && subscriptionItem.finalChargeAttemptDate !== undefined) && <FormattedMessage
                      id="9ScQLT-9uKhatG7U3pAQ"
                      defaultMessage="Membership payment due before {date}"
                      description="Shows the final payment due date for pending LuxPlus membership renewal"
                      values={{ date: <FormatDateTime value={subscriptionItem.finalChargeAttemptDate} format="dmy-casual" /> }}
                    />}
                    {isPendingRenewalAndNotRenewing && intl.formatMessage({
                      id: 'rZ0eJKcQumu2iZj6phe2',
                      defaultMessage: 'Your membership will expire soon',
                      description: 'Warning when LuxPlus membership is pending renewal and not set to auto-renew',
                    })}
                  </BodyText>
                  {overDueInDays > 0 && hasAutoRenewal && <MessagingLabel variant="critical-high">
                    <FormattedMessage
                      id="UPG8NnZL_NxW_V4A_VDM"
                      defaultMessage="{count, plural, one {# day} other {# days}} overdue"
                      description="Label showing how many days overdue a LuxPlus membership renewal payment is"
                      values={{ count: overDueInDays }}
                    />
                  </MessagingLabel>}
                </Group>}
              </Group>
              <Group direction="horizontal" gap={16}>
                {!!confirmedItems?.length && <TextLink size="medium" weight="bold" onClick={handleViewActivity}>
                  {intl.formatMessage({
                    id: 'V7QnHbUTTgVh6f5pw3h-',
                    defaultMessage: 'View activity',
                    description: 'Button to view LuxPlus membership activity and savings history',
                  })}
                </TextLink>}
                <TextLink size="medium" weight="bold" onClick={handleManageMembership}>
                  {intl.formatMessage(luxPlusGlossaryMessages.manageMembership)}
                </TextLink>
              </Group>
              {isCardExpiringBeforeRenewal && <MessageBanner
                kind="warning"
                density="compact"
                heading={intl.formatMessage({
                  id: 'ZKN41dHybX6RDl_2ll2w',
                  defaultMessage: 'Your preferred payment method expires before your next renewal',
                  description: 'Warning heading in LuxPlus+ hero when saved card expires before membership renewal date',
                })}
              >
                <FormattedMessage
                  id="ldO7MAKYhtWevRggzqDr"
                  defaultMessage="Update your payment method before {renewalDate} to keep your member benefits. <link>Update now</link>"
                  description="Warning body in LuxPlus+ hero prompting member to update expiring card before renewal"
                  values={{
                    renewalDate: intlDateFormatter(subscriptionRenewalDate, INTL_DMY_CASUAL_FORMAT),
                    link: (chunks) => <TextLink size="small" onClick={handleUpdatePaymentMethod}>{chunks}</TextLink>,
                  }}
                />
              </MessageBanner>}
            </LeftColumn>
            {children && <RightColumn>{children}</RightColumn>}
          </HeroGrid>
        </LayoutContainer>
      </HeroSheet>

      {isLuxPlusMonthTakeover && <HomeBannerSpacing>
        <HomeBannerLayoutContainer variant="large">
          <LuxPlusMonthTakeoverHomeBanner roundedCorners wideDesktopInset />
        </HomeBannerLayoutContainer>
      </HomeBannerSpacing>}

      <NavSection shadow={!notSticky ? 'bottom-small' : undefined}>
        <LayoutContainer variant="medium">
          <Group direction="horizontal" horizontalAlign="center">
            <SegmentedOptionsGroup
              inputsName="luxPlusMemberLandingTab"
              onValueChange={handleTabChange}
            >
              <SegmentedOption
                checked={activeTab === 'best-offers'}
                value="best-offers"
                title={bestOffersLabel}
              />
              {showPartnerOffersTab && <SegmentedOption
                checked={activeTab === 'partner-offers'}
                value="partner-offers"
                title={intl.formatMessage({
                  id: 's6rLAKUI18p8C6GH0LhS',
                  defaultMessage: 'Partner offers',
                  description: 'Tab label for partner offers on LuxPlus member landing page',
                })}
              />}
              <SegmentedOption
                checked={activeTab === 'all-benefits'}
                value="all-benefits"
                title={intl.formatMessage({
                  id: '8wab0S60B6J3QJyuVwQA',
                  defaultMessage: 'All benefits',
                  description: 'Tab label for all benefits on LuxPlus member landing page',
                })}
              />
            </SegmentedOptionsGroup>
          </Group>
        </LayoutContainer>
      </NavSection>
      <div ref={stickyRef} />

      <LuxPlusLandingNavigationProvider navigateToPartnerOffers={handleTabPartnerOffers} scrollOffset={STICKY_NAV_SCROLL_OFFSET}>
        {activeTab === 'best-offers' && <LuxPlusLoggedInOffersTabSection />}
        {activeTab === 'partner-offers' && showPartnerOffersTab && <LuxPlusLoggedInPartnerOffersTabSection />}
        {activeTab === 'all-benefits' && <LuxPlusLoggedInBenefitsTabSection />}
      </LuxPlusLandingNavigationProvider>
    </>
  )
}

export default LuxPlusMemberLandingLayout
